// src/components/agents/AgentCard.tsx
//
// Card de agente da listagem /agents — markup LITERAL do wireframe §07
// (.agtc/.agtc-top/.agt-av/.agt-name/.agt-slug/.agt-badges/.agt-sw/.agt-info/
// .agt-model/.agt-acts — varredura v2 §5.6-C3). CSS: src/styles/wf-agents.css.
// Avatar redondo com menu de contexto + switch ativar/desativar + selects
// provedor/modelo + ações Ver detalhes (modal) e Promover/Instanciar.

import { useState } from 'react';
import { api, type AgentListItem } from '@/lib/api';
import { Eye, Upload, Download } from 'lucide-react';
import { AgentAvatar } from './AgentAvatar';
import { AgentModelSelect } from './AgentModelSelect';
import {
  ARCHETYPE_LABELS,
  isAgentActive,
  isCompanionProfile,
  parseModelPolicy,
} from './agent-constants';

interface AgentCardProps {
  agent: AgentListItem;
  onViewDetails: (agent: AgentListItem) => void;
  onPromote: (agent: AgentListItem) => void;
  onInstantiate: (agent: AgentListItem) => void;
  /** Notifica a página pra atualizar o item na lista (avatar/status). */
  onUpdated: (agent: AgentListItem) => void;
  onError: (message: string) => void;
  /** AGT-09: feedback de sucesso (toast) — ex: modelo salvo. */
  onNotice?: (message: string) => void;
}

/** Classe do badge de archetype no wireframe: companion=comp, demais=dom. */
function archetypeBadgeClass(archetype: string): string {
  return archetype === 'companion' ? 'comp' : 'dom';
}

export function AgentCard({
  agent,
  onViewDetails,
  onPromote,
  onInstantiate,
  onUpdated,
  onError,
  onNotice,
}: AgentCardProps) {
  const arch = ARCHETYPE_LABELS[agent.archetype] ?? ARCHETYPE_LABELS.domain_worker;
  const active = isAgentActive(agent.status);
  const [toggling, setToggling] = useState(false);

  // Switch ativar/desativar (AGT-01). Endpoint real:
  // - agent: PATCH /v1/agents/:slug { status: 'active' | 'paused' }.
  // - companion: PATCH /v1/companions/:id { is_active }.
  // Otimista com rollback no erro.
  const handleToggle = async (next: boolean) => {
    const prevStatus = agent.status;
    const nextStatus = next ? 'active' : 'paused';
    setToggling(true);
    onUpdated({ ...agent, status: nextStatus });
    try {
      if (isCompanionProfile(agent)) {
        await api.updateCompanion(agent.entity_id ?? agent.id, { is_active: next });
      } else {
        await api.updateAgent(agent.slug || agent.id, { status: nextStatus });
      }
    } catch (err) {
      onUpdated({ ...agent, status: prevStatus });
      onError(
        err instanceof Error
          ? `Não foi possível ${next ? 'ativar' : 'desativar'} o agente: ${err.message}`
          : 'Não foi possível alterar o status do agente.',
      );
    } finally {
      setToggling(false);
    }
  };

  return (
    // AGT-06: clique no CARD abre os detalhes (comportamento esperado pelo
    // usuário — não só o botão "Ver detalhes"). Áreas interativas internas
    // (avatar/menu, switch, selects, botões de ação) fazem stopPropagation.
    // Acessível: role=button + Enter/Espaço quando o foco está no card.
    <div
      className="agtc agtc-click"
      role="button"
      tabIndex={0}
      aria-label={`Abrir detalhes do agente ${agent.name}`}
      onClick={() => onViewDetails(agent)}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
          e.preventDefault();
          onViewDetails(agent);
        }
      }}
    >
      {/* Topo: avatar + nome/slug/badges + switch (wireframe .agtc-top) */}
      <div className="agtc-top">
        <div onClick={(e) => e.stopPropagation()}>
          <AgentAvatar
            agent={agent}
            onAvatarUpdated={(url) => onUpdated({ ...agent, avatar_url: url })}
            onError={onError}
          />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Switch ativar/desativar (wireframe .agt-sw). Flutua à direita
              DENTRO da coluna de texto (sem label textual): a 1ª linha do nome
              contorna o switch e as linhas seguintes aproveitam a largura toda
              abaixo dele — nome longo não espreme slug/badges. */}
          <label
            className="agt-sw"
            data-disabled={toggling || undefined}
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="checkbox"
              role="switch"
              checked={active}
              disabled={toggling}
              onChange={(e) => void handleToggle(e.target.checked)}
              aria-label={`${active ? 'Desativar' : 'Ativar'} agente ${agent.name}`}
            />
            <span className="sw-track" />
          </label>
          <div className="agt-name">{agent.name}</div>
          <div className="agt-slug">{agent.slug}</div>
          <div className="agt-badges">
            <span className={`agt-bdg ${archetypeBadgeClass(agent.archetype)}`}>
              {arch.label.toLowerCase()}
            </span>
            <span className={`agt-bdg ${agent.scope === 'global' ? 'glob' : 'tnt'}`}>
              {agent.scope}
            </span>
          </div>
        </div>
      </div>

      {/* Selects provedor + modelo (AGT-02 — wireframe .agt-model) */}
      {/* AGT-04: model_policy vem da LISTAGEM — `initial` presente evita o GET
          de detalhe por card (N+1 de 2-10s cada que travava a tela). Backend
          antigo (campo ausente) → undefined → fallback fetch preservado. */}
      {/* AGT-09: ref por UUID (entity_id) — slug pode existir em DUAS rows
          (global + clone do tenant, ex: dan-mall) e o PATCH por slug salvava
          numa row enquanto o card mostrava a outra. UUID = row exata. */}
      <AgentModelSelect
        agentRef={agent.entity_id ?? agent.id}
        initial={agent.model_policy !== undefined ? parseModelPolicy(agent.model_policy) : undefined}
        compact
        onError={onError}
        onSaved={(policy) => {
          // AGT-09: persiste a escolha no estado da LISTA (sem isso, paginar
          // e voltar remontava o card com o model_policy velho da listagem)
          // + toast de sucesso (feedback que faltava no save).
          onUpdated({ ...agent, model_policy: policy as Record<string, unknown> });
          onNotice?.(
            `Modelo de ${agent.name} salvo: ${policy.model_key ?? '—'} (${policy.provider ?? '—'})`,
          );
        }}
      />

      {/* Ações: Ver detalhes (modal) + Promover/Instanciar (wireframe .agt-acts) */}
      <div className="agt-acts" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="wf-btn wf-btn-ghost" onClick={() => onViewDetails(agent)}>
          <Eye aria-hidden />
          Ver detalhes
        </button>
        {agent.scope === 'global' ? (
          <button
            type="button"
            className="wf-btn wf-btn-instantiate"
            onClick={() => onInstantiate(agent)}
            title="Instanciar este agente global no seu tenant"
          >
            <Download aria-hidden />
            Instanciar
          </button>
        ) : (
          <button
            type="button"
            className="wf-btn wf-btn-promote"
            onClick={() => onPromote(agent)}
            title="Promover este agente pra global"
          >
            <Upload aria-hidden />
            Promover
          </button>
        )}
      </div>
    </div>
  );
}
